import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PagesModule } from "../../../../pages.module";
import { ApiService } from '../../../../../@core/service/api.service';
import { CacheType, CompilationModel, PaymentPlanInvoice, PaymentPlanItem } from '../../../../../@models/erp/compilation';
import { isValid } from 'date-fns';
import * as saveAs from 'file-saver';
import * as moment from 'moment';
import { getTotalNet, mongoId, toEUR } from '../../../../../@core/common/global';
import { FiscalYearData, InvoicePaymentData, InvoiceStatusData, PaymentDates } from '../../../../../@models/erp/compilationData';
import { RoundPipe } from '../../../../../@theme/pipes';
import { DownloadConfig } from '../../document-printer/document-printer.component';
import { DocMetrics } from '../../line-item-container/line-item-metrics/line-item-metrics.component';
import { SharedDataService } from '../../../../../@core/service/shared-data.service';
import { TreeService } from '../../../../../@core/service/tree.service';
import { Subscription } from 'rxjs';
import { NrkWindowComponent } from '../../../shared/nrk-window/nrk-window.component';
import { DialogWizardService } from '../../../shared/dialog-wizard/dialog-wizard.service';
import { ProjectFinalizationComponent } from './project-finalization/project-finalization.component';
import { NbToastrService } from '@nebular/theme';
import { AuthGuardService } from '../../../../../@core/service/auth-guard.service';


export enum ProfitMarginType {
  cs = 1,
  ocSpo = 2,  
}

@Component({
  selector: 'ngx-profit-margin',
  templateUrl: './profit-margin.component.html',
  styleUrls: ['./profit-margin.component.scss'],
})
export class ProfitMarginComponent implements OnInit, OnDestroy {

  @Input() compilation: CompilationModel;
  @Input() type = ProfitMarginType.cs;
  @Input() withChecklist = false;
  @Input() withWindow = true;
  @Input() disabled = true;

  @ViewChild('profitCalcTable') profitCalcTable: ElementRef<HTMLElement>;
  @ViewChild('profitCSSPOTable') profitCSSPOTable: ElementRef<HTMLElement>;

  @ViewChild('profitCalc') profitCalc: NrkWindowComponent;
  @ViewChild('profitOCSPO') profitOCSPO: NrkWindowComponent;

  protected api = inject(ApiService);
  protected data = inject(SharedDataService);
  protected tree = inject(TreeService);
  protected wizard = inject(DialogWizardService);
  protected toastr = inject(NbToastrService);
  protected auth = inject(AuthGuardService);
  protected cdr = inject(ChangeDetectorRef);

  print = false;

  paymentDates = PaymentDates.getOptions();
  fiscalYearOptions = FiscalYearData.getOptions();
  marginType = ProfitMarginType;
  moment = moment;

  ocSpoProfitData = {} as any;  
  withMarkup = true;
  partiallyAssigned = false;

  profitMarginData: any = {}
  importDuty = 1.7; // percent
  otherCosts = 3; // percent


  linkedPfspos: CompilationModel[];
  linkedFC: CompilationModel;
  linkedOF: CompilationModel;
  linkedCsSpoCodes: string[];

  constructor() { }

  tev: Subscription;
  ngOnInit() {

    this.data.loadCompilationData().then(() => {
      this.calcProfitMarginData();
    });

    this.tev = this.tree.documentTreeLoadedEvent.subscribe(t => {
      this.calcProfitMarginData();
    });

  }

  ngOnDestroy() {
    this.tev.unsubscribe();
  }

  show() {
    if (this.type === ProfitMarginType.cs) {
      this.profitCalc.show();
    } else {
      this.profitOCSPO.show()
    }
  }

  async calcProfitMarginData() {

    this.calcOcSpoProfitMarginData();

    // console.log('calcProfitMarginData', this.compilation.code, this.compilation, this.compilation.shipmentLineItems);

    if (!this.compilation.shipmentLineItems?.length || !this.compilation.shipmentLineItems[0].document?.lineItems) return

    this.profitMarginData.shipmentLineItems = await Promise.all(this.compilation.shipmentLineItems.flatMap(sli => {
      return sli.document.lineItems?.map(li => {
        return new Promise<any>((res) => {
          this.api.loadPrice(
            sli.document.issuer,
            mongoId(sli.document.customerReference),
            mongoId(li.finishedProduct),
            sli.document.arrivingVieDate,
            'EUR', // sli.document.currency,
            true
          ).then(price => {
            const metrics = new DocMetrics(this.api, this.compilation, li, new RoundPipe(), this.data, true);
            const pcPrice = price ? metrics.metrics.convert(price.value || 0, 1, +price.type) : 0;
            // console.log(`
            //   issuer: ${sli.document.issuer},
            //   business: ${mongoId(sli.document.customerReference)},
            //   finished product: ${mongoId(li.finishedProduct)},
            //   exw date: ${sli.document.exwJapanDate},
            //   arriving date: ${sli.document.arrivingVieDate},
            //   currency: EUR, ${sli.document.currency},
            //   sales: ${false},
            //   price: ${pcPrice * li.displayUnitQuantity},
            // `);
            res({
              recipient: this.data.businesses.find(f => mongoId(f) === mongoId(sli.document.recipient))?.name,
              code: sli.code, 
              customer: this.data.businesses.find(f => mongoId(f) === mongoId(sli.document.customerReference))?.name,
              product: `${li.finishedProduct?.calcCode}; ${li.finishedProduct?.calcName}`,
              currency: price?.currency || '',
              sellingNet: pcPrice * li.quantity
            })
          })
        })
      })
    }))

    this.profitMarginData.salesSum = this.profitMarginData.shipmentLineItems.reduce((a, c) => a + c?.sellingNet, 0);

    const fx = this.data.compilationData.currency.sort((a,b) => a.validFrom > b.validFrom ? 1 : -1);
    // console.log('exchange rates', fx);

    this.profitMarginData.buyingSum = this.compilation.shipmentLineItems.flatMap(sli => 
      sli.document.lineItems?.map(li => ({
        date: sli.document.exwJapanDate,
        currency: sli.document.currency,
        net: li.quantity * li.piecePrice
      }))
    ).reduce((a, c, idx) => {
      const fxs = this.data.compilationData.currency.sort((a,b) => a.validFrom < b.validFrom ? 1 : -1)
      const fx = fxs.find(f => f.validFrom < c?.date) || fxs[0];
      const netEur = c?.currency.toLowerCase() === 'eur' ? c.net : (c ? c.net / +fx[c.currency.toLowerCase()] : 0);
      // console.log('selected fx:', c?.date, fx.validFrom, c?.net, netEur, a);
      this.profitMarginData.shipmentLineItems[idx].buyingNet = netEur;
      this.profitMarginData.shipmentLineItems[idx].percent = this.profitMarginData.shipmentLineItems[idx].sellingNet / this.profitMarginData.shipmentLineItems[idx].buyingNet - 1;
      return a + netEur;
    }, 0)

    // console.log('spo payment:', this.compilation.spoPayment);

    this.profitMarginData.supplierInvoiceSum = this.compilation.spoPayment.reduce((a,c) => {
      const fxs = this.data.compilationData.currency.sort((a,b) => a.validFrom < b.validFrom ? 1 : -1)
      const date = c.spInvoiceDate || this.compilation.exwJapanDate;
      const fx = fxs.find(f => f.validFrom < date) || fxs[0];
      const spInvoiceEur = c?.currency?.toLowerCase() === 'eur' || (!c?.currency && this.compilation.currency?.toLowerCase() === 'eur') ? c.spInvoiceValue : c.spInvoiceValue / +fx[(c.currency || this.compilation.currency).toLowerCase()];
      console.log('spo payment', fxs, date, fx, spInvoiceEur, c?.currency);
      return a + (spInvoiceEur ?? 0)
    }, 0)

    this.profitMarginData.importDuty = (this.profitMarginData.buyingSum + this.profitMarginData.supplierInvoiceSum) * this.importDuty / 100;

    this.profitMarginData.otherCosts = (this.profitMarginData.buyingSum + this.profitMarginData.supplierInvoiceSum + this.profitMarginData.importDuty) * this.otherCosts / 100;

    this.profitMarginData.sum = 
      this.profitMarginData.salesSum
      - this.profitMarginData.buyingSum
      - this.profitMarginData.supplierInvoiceSum
      - this.profitMarginData.importDuty
      - this.profitMarginData.otherCosts

    this.profitMarginData.percentTotal = this.profitMarginData.sum / this.profitMarginData.salesSum;
    this.profitMarginData.totalNetWeight = this.compilation?.shipmentLineItems?.reduce((a, sli) => 
      a + sli.document?.lineItems.reduce((ad, li) => 
          ad + (
            li?.finishedProduct?.content?.energy && li?.finishedProduct?.product
              ?
                (li.finishedProduct.content.energy.width / 1000)
                * li.finishedProduct.content.energy.rollLength
                * (li.finishedProduct.product.weight / 1000)
                * li.quantity
              :
                0
          )
      , 0)
    , 0)

    console.log('profit margin data', this.profitMarginData);

  }

  async calcOcSpoProfitMarginData() {

    const eur = (date: Date, curr: string, val: number) => toEUR(date, curr, val, this.data);

    const ocPayments = this.compilation.paymentPlan?.items?.flatMap(m => 
      m.invoices?.map((inv: PaymentPlanInvoice) => ({
        text: inv.code,
        payment: InvoicePaymentData.get(inv.paymentType)?.name ?? '',
        date: new Date(inv.issueDate).toISOString().replace(/T.*/, ''),
        status: InvoiceStatusData.get(inv.paymentStatus)?.name ?? '',
        value: inv.net,
      }))
    ).filter(notNull => notNull);
    // const ocPayments = this.compilation.paymentPlan?.items?.flatMap(m => m.invoices).filter(notNull => notNull);

    // const spoPayments = this.tree.linkedSPO?.spoPayment?.map(m => ({
    //   text: m.spInvoice,
    //   date: new Date(m.spInvoiceDate).toISOString().replace(/T.*/, ''),
    //   value: m.spInvoiceValue
    // }))

    this.linkedPfspos = this.tree.linkedPfspos ?? (await this.api.getCache(CacheType.spos, { code: this.compilation.code}))?.docs;
    
    const tree = this.tree.linkedOC?.code === this.compilation?.code ? this.tree : (await this.api.getCache(CacheType.linkedDocs, {code: this.compilation.code})).docs;
    this.linkedFC = tree.linkedFC;
    this.linkedOF = tree.linkedOF;
    this.linkedCsSpoCodes = tree.linkedCsSpoCodes;

    console.log('calcOcSpoProfitMarginData', this.linkedPfspos);

    const spoPayments = this.linkedPfspos?.filter(notNull => notNull).flatMap(spo => spo.spoPayment?.map(m => ({
      text: m.spInvoice,
      date: isValid(m.spInvoiceDate) ? new Date(m.spInvoiceDate).toISOString().replace(/T.*/, '') : '',
      value: eur(m.paymentDate, m.currency || spo.currency, m.spInvoiceValue),
    })))

    console.log('spo payments', spoPayments, this.linkedPfspos);

    const payments = Array.from({ length: Math.max(ocPayments?.length ?? 0, spoPayments?.length ?? 0) }).map((m, idx) => {
      const dueDate = this.tree.invoiceDocs?.find(f => f.code === ocPayments?.[idx]?.text)?.dueDate;
      return {
        oc: ocPayments?.[idx] ?? { text: '', value : 0 },
        ocStatus: ocPayments?.[idx]?.status !== 'fully paid' ? {
          text: moment().isAfter(moment(dueDate).add(1, 'week'))
            ? 'overdue'
            : moment().isAfter(moment(dueDate))
              ? 'grace'
              : 'due',
          style:  moment().isAfter(moment(dueDate).add(1, 'week'))
            ? 'color: #cc3333'
            : '',
          // text: ,
          // style: 'color: #ff6666',
        } : { text: 'fully paid', color: '' }, 
        // ocPayments?.[idx]?.status === 'fully paid',
        spo: spoPayments?.[idx] ?? { text: '', value : 0 }
      }
    });

    const sumOc = -(ocPayments?.reduce((a, c) => a + (c?.value ?? 0), 0) ?? 0);
    const sumSpo = spoPayments?.reduce((a, c) => a + (c?.value ?? 0), 0) ?? 0;

    const ocTotal = (this.data.calcData(this.compilation)?.totalNet ?? 0) + sumOc;
    const spoTotal = this.data.calcData(this.partiallyAssigned 
      ? this.tree.ocSpoLineItemMap
      : this.linkedPfspos, { toEUR: true, date: this.linkedPfspos?.[0]?.exwJapanDate })?.totalNet ?? 0;

    const remainingOc = ocTotal - sumOc;
    const remainingSpo = spoTotal - sumSpo;

    let projectStatus = 'Pending'
    if (remainingOc === 0 && remainingSpo !== 0) projectStatus = 'All OC Paid';
    if (remainingOc !== 0 && remainingSpo === 0) projectStatus = 'All SPO Paid';
    if (this.compilation.projectCompleted || (remainingOc === 0 && remainingSpo === 0)) projectStatus = 'Project Complete'

    this.ocSpoProfitData = {
      oc: this.compilation?.code ?? '',
      spo: this.linkedPfspos?.map(spo => spo?.code).join(', ') || '',
      ocTotal,
      spoTotal,
      payments,
      sumOc,
      sumSpo,
      remainingOc,
      remainingSpo,
      expectedMarkup: ocTotal - spoTotal,
      expectedMarkupPercent: 1 - spoTotal / ocTotal,
      currentMarkup: sumOc - sumSpo,
      currentMarkupPercent: 1 - sumSpo / sumOc,
      projectStatus,
      nextInvoiceMilestone: (this.compilation.paymentPlan?.items as PaymentPlanItem[])?.find(f => !f.invoices?.length)?.textPrefix
    };

    console.log('calcOcSpoProfitMarginData', ocPayments, spoPayments, payments, this.ocSpoProfitData);
    
  }

  async closeGS() {
    const doc = await this.api.loadCompilation(mongoId(this.compilation));
    if (doc) {
      this.compilation.csCompleted = doc.csCompleted = this.compilation.csCompleted ? false : true;
      this.api.saveCompilation(doc);
    }
  }

  async downloadProfitCalcPdf(spoOc = false) {

    const date = moment(new Date()).format('YYMMDD');
    const customer = this.data.getBusinessName(this.compilation.customerReference);
    const span = (y: number) => `${(y ?? 0).toString().slice(-2)}-${((y ?? 0) + 1).toString().slice(-2)}`;
    const docnum = this.compilation.nameOverride ?? this.compilation.code;
    const pdfName = `${date}_${customer}_PM_${docnum}_FY${span(this.compilation.fiscalYear)}.pdf`;

    this.print = true;
    // this.cdr.detectChanges();
    await new Promise((res) => { // await change detection
      setTimeout(() => res(true));
    })

    const head = `
      <head>
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: Pero, Verdana, Arial;
          }
          .profit-calc table {
            border-collapse: collapse;
            margin: 0.2rem auto 1.5rem auto;
            font-size: 11pt;
          }
          .profit-calc th {
            background-color: #ddd;
          }
          .profit-calc th, .profit-calc td {
            border: 1px solid #ddd;
            padding: 0.5rem;
          }
          .container {
            display: -webkit-box;
            -webkit-box-pack: justify; /* space between horizontally */
            -webkit-box-align: end;    /* align to bottom */
          }
          .left {
            text-align: left;
          }
          .right {
            text-align: right;
          }
          .profit-layout-table th, td {
            border: 0;
            margin: 0;
            padding: 0 2rem 0 0;
            text-align: left;
          }
          .profit-layout-table tr:first-child td {
            font-size: smaller;
            color: gray;
            line-height: 0.8rem;
          }
          .layout-label, .label {
            font-size: smaller;
            color: gray;
            padding-right: 0.5rem;
          }
          .section-header {
            font-weight: bold;
            font-size: 1.1rem;
            padding-top: 1.75rem;
            padding-bottom: 0.5rem;
          }  
          .info-segment-new {
            width: 100%;
            max-width: 50rem;
            margin-bottom: 5mm;
          }
          .info-segment-new ul {
            display: table;
            width: 100%;
            table-layout: fixed;
            border-collapse: collapse; /* Removes extra spacing */
            padding: 0;
            margin: 0;
          }
          .info-segment-new li {
            display: table-cell;
            vertical-align: top;
            padding-right: 0.5rem;
          }
          .info-segment-new td {
            margin: 0;
            padding: 0;
          }
          .checklist table {
            margin: 0 auto;
            font-size: 1.8rem;
            line-height: normal;
          }
          .checklist h4 {
            padding-top: 3rem;
            padding-bottom: 0.8rem;
            text-align: center;
            font-size: 2.5rem;
            font-weight: bold;
          }
          .checklist table th:nth-child(-n+2),
          .checklist table td:nth-child(-n+2) {
            text-align: center;
            height: 4rem;
            padding-right: 2rem;
          }
        </style>
      </head>
    `

    const html = spoOc 
      ? 
      `<html>${head}<body>
          ${this.profitCSSPOTable.nativeElement.innerHTML}
        </body></html>
      `
      :
      `
        <html>${head}<body><div class="profit-calc">
        <div style="text-align:center;">
          <br>
          <h2>Grouped Shipment Profit Margin ${this.compilation.code}</h2>
        </div>
        <div class="container">
          <div class="left">
            PO#: ${this.compilation.customerDocumentNumber}
          </div>
          <div class="right">
            EXW ${new Date(this.compilation.exwDate).toISOString().replace(/T.*/, '')} /
            ARR ${new Date(this.compilation.arrivingVieDate).toISOString().replace(/T.*/, '')}
            <br />
            Carrier / Method: ${this.compilation.meansOfTransport}
          </div>
        </div>      
        <div>
          ${this.profitCalcTable.nativeElement.innerHTML}
        </div></body></html>
      `
    const config = new DownloadConfig();
    // config.css = 'document-printer.css';
    config.footer = `
      <!DOCTYPE html>
      <html>

      <head>
        <script>
          function subst() {
            var vars = {};
            var query_strings_from_url = document.location.search.substring(1).split('&');
            for (var query_string in query_strings_from_url) {
              if (query_strings_from_url.hasOwnProperty(query_string)) {
                var temp_var = query_strings_from_url[query_string].split('=', 2);
                vars[temp_var[0]] = decodeURI(temp_var[1]);
              }
            }
            var css_selector_classes = ['page', 'frompage', 'topage', 'webpage', 'section', 'subsection', 'date', 'isodate', 'time', 'title', 'doctitle', 'sitepage', 'sitepages'];
            for (var css_class in css_selector_classes) {
              if (css_selector_classes.hasOwnProperty(css_class)) {
                var element = document.getElementsByClassName(css_selector_classes[css_class]);
                for (var j = 0; j < element.length; ++j) {
                  element[j].textContent = vars[css_selector_classes[css_class]];
                }
              }
            }
          }
        </script>
      </head>

      <body style="border:0; margin: 2rem 0 2rem 0;" onload="subst()">
        <div style="border-top: 1px solid #a70240; width: 100%;font-size:11pt; padding: 0.5rem 0 0 0">
          <table style="width: 100%">
            <tr>
              <td>Date: ${new Date().toISOString().replace(/T.*/, '')}<td>
              <td style="text-align:center">${pdfName}</td>
              <td style="text-align:right">
                Page <span class="page"></span> of <span class="topage"></span>
              </td>
            </tr>
          </table>
        </div>
      </body>

      </html>`;   
    config.html = html
    config.orientation = 'portrait';

    console.log('html', html)

    await new Promise(resolve => this.api.getDocumentFile('/api/v1/compilation/pdf', config).subscribe(async res => {
      saveAs(res, pdfName);
      this.print = false;
    }));
    

  }


  downloadProfitCalcXls() {
    function toDecimal(value) {
      return +value.toFixed(2);
    }
    const conf = new DownloadConfig();
    const cols = [
      'Recipient',
      'SPO',
      'Customer',
      'Product #',
      'Product Name',
      'Product Dimensions',
      'Purchase',
      'Sale'
    ]
    const table = [
      ...this.profitMarginData.shipmentLineItems.map(m => 
        [
          m.recipient,
          m.code,
          m.customer,
          m.product.split(/; */)?.[0] ?? '',
          m.product.split(/; */)?.[1] ?? '',
          m.product.split(/; */)?.[2] ?? '',
          toDecimal(m.buyingNet),
          toDecimal(m.sellingNet),
        ]
      ),
      [
        'CS Total NET',
        '', '', '', '', '',
        toDecimal(this.profitMarginData.buyingSum),
        toDecimal(this.profitMarginData.salesSum)
      ],
      [],
      [
        'CS Purchasing Sum',
        '', '', '', '', '', '',
        toDecimal(-this.profitMarginData.buyingSum)
      ],
      [
        'Transport & Handling',
        '', '', '', '', '', '',
        toDecimal(-this.profitMarginData.supplierInvoiceSum)
      ],
      [
        'Import Duty',
        '', '', '', '', '', '',
        toDecimal(-this.profitMarginData.importDuty)
      ],
      [
        'Other Costs',
        '', '', '', '', '', '',
        toDecimal(-this.profitMarginData.otherCosts)
      ],
      [],
      [
        'Profit Margin',
        '', '', '', '', '', '',
        toDecimal(this.profitMarginData.sum)
      ]
    ]
    conf.table = JSON.stringify({
      header: cols.map(m => ({ name: m, header: m })),
      table: table,
    })
    this.api.getDocumentFile('/api/v1/document_report/xls', conf).subscribe(async res => {
      const date = moment(new Date()).format('YYMMDD');
      const docnum = this.compilation.nameOverride ?? this.compilation.code;
      saveAs(res, `${date}_${docnum}_Profit_Margin.xls`);
    });
  }


  getFiscalYear() {
    return this.fiscalYearOptions.find(f => +f.id === +this.compilation?.fiscalYear)?.name
  }

  async finalizeProject(completed = true) {
    const result = completed ? await this.wizard.openWizard(ProjectFinalizationComponent, {data: { compilation: this.compilation } }) : true;
    if (result && this.compilation._id) {
      const doc = await this.api.loadCompilation(mongoId(this.compilation));
      doc.projectCompleted = this.compilation.projectCompleted = completed;
      this.api.saveCompilation(doc).then(() => {
        if (completed) {
          this.toastr.success('Project successfully finalized', 'Project Finalized');
        } else {
          this.toastr.warning('Project completion reverted', 'Revert Completion');
        }
      });
    }
  }

  async saveProfitMarginText() {
    const doc = await this.api.loadCompilation(mongoId(this.compilation));
    // doc.profitMarginText = this.compilation.profitMarginText;
    doc.projectRemark = this.compilation.projectRemark;
    console.log('saving doc', doc);
    this.api.saveCompilation(doc).then(() => {
      this.toastr.success('Remarks sucessfully saved', 'Profit Margin');
    });
  }

}